import { useEffect, useMemo } from "react";

import {
  createGoogleOAuthPopupSnapshot,
  readGoogleOAuthPopupMessage,
  writeGoogleOAuthPopupSnapshot,
} from "../lib/googleOAuthPopup";

function tryCloseWindow() {
  try {
    window.close();
  } catch {
    // noop
  }

  try {
    window.open("", "_self");
    window.close();
  } catch {
    // noop
  }
}

function getHeadline(googleConnect: "success" | "error" | null) {
  if (googleConnect === "error") {
    return "Googleアカウントとの連携を完了できませんでした";
  }

  if (googleConnect === "success") {
    return "Googleアカウントとの連携が完了しました";
  }

  return "Googleアカウントとの連携を確認しています";
}

function getDescription(googleConnect: "success" | "error" | null) {
  if (googleConnect === "error") {
    return "この画面は自動で閉じます。閉じない場合は、このウィンドウを閉じて元の画面で状態を確認してください。";
  }

  return "この画面は自動で閉じます。閉じない場合は、このウィンドウを閉じて元の画面へ戻ってください。";
}

function getErrorDetailMessage(errorCode?: string | null) {
  switch (errorCode) {
    case "access_denied":
      return "Googleアカウントとの連携がキャンセルされました。";
    case "invalid_state":
      return "Googleアカウントとの連携確認情報が一致しませんでした。";
    case "missing_code":
      return "Googleから認可コードを受け取れませんでした。";
    case "token_exchange_failed":
      return "Googleアカウントとの認可に失敗しました。";
    case "missing_refresh_token":
      return "必要な権限の確認ができませんでした。";
    case "missing_required_scope_in_bundle":
      return "必要なGoogle権限が不足しています。";
    default:
      return "Googleアカウントとの連携に失敗しました。";
  }
}

export default function GoogleOAuthPopupComplete() {
  const payload = useMemo(() => readGoogleOAuthPopupMessage(window.location.search), []);
  const snapshot = useMemo(() => createGoogleOAuthPopupSnapshot(payload), [payload]);

  useEffect(() => {
    writeGoogleOAuthPopupSnapshot(snapshot);

    if (window.opener) {
      try {
        window.opener.postMessage(snapshot, window.location.origin);
      } catch {
        // noop
      }
    }

    tryCloseWindow();

    const timer = window.setTimeout(() => {
      tryCloseWindow();
    }, 300);

    const timer2 = window.setTimeout(() => {
      tryCloseWindow();
    }, 1000);

    return () => {
      window.clearTimeout(timer);
      window.clearTimeout(timer2);
    };
  }, [snapshot]);

  return (
    <main className="min-h-screen bg-[radial-gradient(circle_at_top,_rgba(250,233,220,0.95),_rgba(255,249,245,1)_55%)] px-6 py-10 text-[#4A3E39]">
      <div className="mx-auto flex min-h-[calc(100vh-5rem)] max-w-md items-center justify-center">
        <div className="w-full rounded-[2rem] border border-white/80 bg-white/90 p-6 text-center shadow-[0_24px_60px_rgba(124,90,78,0.16)] backdrop-blur-sm">
          <p className="text-[12px] font-black uppercase tracking-[0.18em] text-[#A67C52]">Complete</p>
          <h1 className="mt-2 text-2xl font-black text-[#7C5A4E]">{getHeadline(snapshot.googleConnect)}</h1>
          <p className="mt-3 text-sm leading-6 text-[#6B5851]">{getDescription(snapshot.googleConnect)}</p>
          {snapshot.googleConnect === "error" && snapshot.errorCode ? (
            <p className="mt-3 text-xs font-bold text-[#A1493E]">
              {getErrorDetailMessage(snapshot.errorCode)} (code: {snapshot.errorCode})
            </p>
          ) : null}
          {snapshot.googleConnect === "error" && !snapshot.errorCode ? (
            <p className="mt-3 text-xs font-bold text-[#A1493E]">{getErrorDetailMessage(null)}</p>
          ) : null}
          <button
            type="button"
            onClick={tryCloseWindow}
            className="mt-5 inline-flex items-center justify-center rounded-full border border-[#E4D5C8] bg-white px-4 py-2 text-xs font-bold text-[#6A4F44] transition hover:bg-[#FFF8F1]"
          >
            閉じる
          </button>
        </div>
      </div>
    </main>
  );
}
